<template>
    <transition name="fade" mode="out-in">
        <div v-if="isOpen" class="popup-rodo-information">
            <button
                type="button"
                class="popup-rodo-information__close-popup"
                @click="$emit('close')"
            />
            <div class="popup-rodo-information__content">
                <div
                    v-for="(paragraph, index) in paragraphs"
                    :key="index"
                    class="popup-rodo-information__row"
                >
                    <p>{{ $t(`${TRANSLATION_KEY}.${paragraph.header}`) }}</p>
                    <template v-if="paragraph.type === 'html'">
                        <span
                            v-html="
                                $t(`${TRANSLATION_KEY}.${paragraph.content}`)
                            "
                        />
                    </template>
                    <template v-else-if="paragraph.type === 'list'">
                        <ol type="a">
                            <li v-for="(item, i) in paragraph.items" :key="i">
                                {{ $t(`${TRANSLATION_KEY}.${item}`) }}
                            </li>
                        </ol>
                    </template>
                    <template v-else-if="paragraph.type === 'mixed'">
                        <template
                            v-for="(block, blockIndex) in paragraph.blocks"
                            :key="blockIndex"
                        >
                            <span
                                v-if="block.type === 'html'"
                                v-html="
                                    $t(`${TRANSLATION_KEY}.${block.content}`)
                                "
                            />
                            <ol v-else-if="block.type === 'list'" type="a">
                                <li v-for="(item, i) in block.items" :key="i">
                                    {{ $t(`${TRANSLATION_KEY}.${item}`) }}
                                </li>
                            </ol>
                        </template>
                    </template>
                </div>
                <div
                    v-if="hasLastUpdated"
                    class="popup-rodo-information__last-updated"
                >
                    <p>{{ $t(`${TRANSLATION_KEY}.lastUpdated`) }}</p>
                </div>
            </div>
        </div>
    </transition>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
    name: 'RodoInformation',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close'],
    setup() {
        const TRANSLATION_KEY = 'RodoInformation';
        const { t } = useI18n();

        const hasTranslation = (item: string) => {
            const key = `${TRANSLATION_KEY}.${item}`;
            const translation = String(t(key) || '');
            return translation.trim() !== '' && translation !== key;
        };

        const hasLastUpdated = computed(() => hasTranslation('lastUpdated'));

        // Define paragraphs structure
        const paragraphs = computed(() => {
            return [
                {
                    header: 'paragraphOneHeader',
                    content: 'paragraphOneContent',
                    type: 'html',
                },
                {
                    header: 'paragraphTwoHeader',
                    content: 'paragraphTwoContent',
                    type: 'html',
                },
                {
                    header: 'paragraphThreeHeader',
                    type: 'list',
                    items: [
                        'paragraphThreeContentOne',
                        'paragraphThreeContentTwo',
                        'paragraphThreeContentThree',
                        'paragraphThreeContentFour',
                    ].filter((item) => {
                        const key = `${TRANSLATION_KEY}.${item}`;
                        const translation = String(t(key) || '');
                        return translation.trim() !== '' && translation !== key;
                    }),
                },
                {
                    header: 'paragraphFourHeader',
                    content: 'paragraphFourContent',
                    type: 'html',
                },
                {
                    header: 'paragraphFiveHeader',
                    content: 'paragraphFiveContent',
                    type: 'html',
                },
                {
                    header: 'paragraphSixHeader',
                    content: 'paragraphSixContent',
                    type: 'html',
                },
                {
                    header: 'paragraphSevenHeader',
                    type: 'mixed',
                    blocks: [
                        ...(hasTranslation('paragraphSevenContentOne')
                            ? [
                                  {
                                      type: 'html',
                                      content: 'paragraphSevenContentOne',
                                  },
                              ]
                            : []),

                        ...([
                            'paragraphSevenContentTwo',
                            'paragraphSevenContentThree',
                        ].filter(hasTranslation).length
                            ? [
                                  {
                                      type: 'list',
                                      items: [
                                          'paragraphSevenContentTwo',
                                          'paragraphSevenContentThree',
                                      ].filter(hasTranslation),
                                  },
                              ]
                            : []),

                        ...(hasTranslation('paragraphSevenContentFour')
                            ? [
                                  {
                                      type: 'html',
                                      content: 'paragraphSevenContentFour',
                                  },
                              ]
                            : []),

                        ...([
                            'paragraphSevenContentFive',
                            'paragraphSevenContentSix',
                            'paragraphSevenContentSeven',
                            'paragraphSevenContentEight',
                            'paragraphSevenContentNine',
                        ].filter(hasTranslation).length
                            ? [
                                  {
                                      type: 'list',
                                      items: [
                                          'paragraphSevenContentFive',
                                          'paragraphSevenContentSix',
                                          'paragraphSevenContentSeven',
                                          'paragraphSevenContentEight',
                                          'paragraphSevenContentNine',
                                      ].filter(hasTranslation),
                                  },
                              ]
                            : []),
                    ],
                },
                {
                    header: 'paragraphEightHeader',
                    content: 'paragraphEightContent',
                    type: 'html',
                },
                {
                    header: 'paragraphNineHeader',
                    content: 'paragraphNineContent',
                    type: 'html',
                },
                {
                    header: 'paragraphTenHeader',
                    content: 'paragraphTenContent',
                    type: 'html',
                },
                {
                    header: 'paragraphElevenHeader',
                    content: 'paragraphElevenContent',
                    type: 'html',
                },
            ];
        });

        return {
            TRANSLATION_KEY,
            paragraphs,
            hasLastUpdated,
        };
    },
});
</script>
